"use client";

import { create } from "zustand";

export type AuthStatus =
  | "idle"
  | "connecting"
  | "signing"
  | "authenticated"
  | "unauthenticated"
  | "error";

export type TraderActivationStatus =
  | "idle"
  | "checking"
  | "activated"
  | "notActivated"
  | "error";

interface AuthState {
  authority: string | null;
  status: AuthStatus;
  error: string | null;
  activationStatus: TraderActivationStatus;
  activationError: string | null;
}

interface AuthActions {
  setConnecting: () => void;
  setSigning: (authority: string) => void;
  setAuthenticated: (authority: string) => void;
  setUnauthenticated: () => void;
  setAuthError: (message: string) => void;
  setActivationChecking: () => void;
  setActivationStatus: (
    status: Exclude<TraderActivationStatus, "checking">,
    error?: string | null
  ) => void;
}

export const useAuthStore = create<AuthState & AuthActions>((set) => ({
  authority: null,
  status: "idle",
  error: null,
  activationStatus: "idle",
  activationError: null,

  setConnecting: () =>
    set({
      status: "connecting",
      error: null,
      activationStatus: "idle",
      activationError: null,
    }),

  setSigning: (authority) =>
    set({
      authority,
      status: "signing",
      error: null,
      activationStatus: "idle",
      activationError: null,
    }),

  setAuthenticated: (authority) =>
    set({
      authority,
      status: "authenticated",
      error: null,
    }),

  setUnauthenticated: () =>
    set({
      authority: null,
      status: "unauthenticated",
      error: null,
      activationStatus: "idle",
      activationError: null,
    }),

  setAuthError: (message) =>
    set({
      status: "error",
      error: message,
      activationStatus: "idle",
      activationError: null,
    }),

  setActivationChecking: () =>
    set({
      activationStatus: "checking",
      activationError: null,
    }),

  setActivationStatus: (activationStatus, activationError = null) =>
    set({
      activationStatus,
      activationError,
    }),
}));
